import { AnyObject } from '@store/types';
import React from 'react';
import { inject, observer } from 'mobx-react';
import { observable, makeObservable } from 'mobx';
import autobind from 'autobind';
import { v4 as uuid } from 'uuid';

import { DatePicker, DatePickerProps } from '@components/datePickers';
import { ProfileForm } from '@profile/store/ProfileForm';
import { Ehr } from '@patient/store/ehr/Ehr';
import { AppState } from '@store/App/App';
import { EhrApi } from '@api/EhrApi';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { translate } from '@chaika/i18n/helper';

interface Props extends Partial<DatePickerProps> {
    name: string;
}

interface StoreProps {
    ehr: Ehr;
    profileForm: ProfileForm;
    app: AppState;
}

@observer
class Component extends React.Component<Props & StoreProps> {
    @observable
    private showValidStatus: boolean = false;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const { ehr, name, profileForm, ...props } = this.props;

        const birthDate = profileForm.getDateBirthDate();

        const isValid = profileForm.isFieldValid(name);

        return React.createElement(DatePicker, {
            ...props,
            name,
            isValid,
            dataTest: `profilePage_${name}`,
            maxDate: new Date(),
            selected: birthDate,
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            onChange: this.handleDateChange,
            showValidStatus: this.showValidStatus,
        });
    }

    @autobind
    protected async handleDateChange(newDate: Date | string) {
        await this.updateFieldValue(newDate as string);
    }

    @autobind
    protected async handleFocus() {
        this.showValidStatus = true;
    }

    @autobind
    protected async handleBlur() {
        this.showValidStatus = false;
    }

    private async updateFieldValue(value: string) {
        const { ehr, name, profileForm, app } = this.props;
        const reqId = uuid();

        const oldValue = profileForm.getFormattedFieldValue(name);

        profileForm.setFieldValue(name, value);
        const newValue = profileForm.getFormattedFieldValue(name);

        if (profileForm.isFieldValid(name)) {
            this.showValidStatus = false;
        }

        if (oldValue !== newValue && profileForm.isFieldValid(name)) {
            try {
                app.addSaveRequest(reqId);
                await EhrApi.update(ehr.id, { [name]: DateHelper.format(value, DateFormat.DateWithDashSeparator) });

                (ehr as AnyObject)[name] = value;

                app.removeSaveRequest(reqId);
            } catch (e: any) {
                console.error(e);
                const [day, month, year] = String(oldValue).split('.');
                const oldDate = new Date(Number(year), Number(month) - 1, Number(day));

                ehr.birthDate = oldDate.toISOString();
                profileForm.setFieldValue(name, oldDate);
                app.showingPopupParams = {
                    title: translate('errorOnSaveData'),
                    description: e.name === 'Conflict' ? translate('ehrHasDuplicateCantChange') : e.message,
                };
            }
        }
    }
}

export const BirthDateInputContainer = inject<Props, StoreProps>(Ehr.Name, ProfileForm.Name, AppState.Name)(Component);
